import React from "react";
import { FeatureRequest } from "interfaces/feature-request";
import styled from "styled-components";
import FeatureRequestTableRow from "./FeatureRequestTableRow";
import { useSorting } from "../../hooks/useSorting";
import { useHistory, useLocation } from "react-router-dom";
import styles from "styles/styles";

const columns = ["name", "state", "updatedAt", "createdAt", "businessArea"];
const sortableColumns: (keyof FeatureRequest)[] = ["name", "state", "updatedAt", "createdAt"];

export const Table = styled.table`
    background-color: ${styles.colors.grey3};
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
`;

const TableHeader = styled.th<{ sortable: boolean }>`
    text-align: left;
    width: ${100 / columns.length}%;
    font-size: 16px;
    font-weight: 500;
    padding: 1rem;
    cursor: ${(props) => (props.sortable ? "pointer" : "default")};
`;

const SortIndicator = styled.span<{
    direction: "asc" | "desc";
}>`
    display: inline-block;
    position: relative;
    top: -2px;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 5px solid rgb(62, 86, 96);
    margin-left: 5px;
    transition: transform 0.2s ease-in-out;
    transform: ${(props) => (props.direction === "desc" ? "rotate(180deg)" : "rotate(0deg)")};
`;

const TableRow = styled.tr`
    background-color: ${styles.colors.white};
    border-bottom: 1px solid ${styles.colors.grey3};
    cursor: pointer;
    transition: background-color 0.2s ease-in-out;

    &:hover {
        background-color: ${styles.colors.lightGrey};
    }

    &:last-child {
        border-bottom: none;
    }
`;

const TableCell = styled.td`
    padding: 1rem;
    font-size: 16px;
    font-weight: 500;
`;

const FeatureRequestsTable: React.FC<{
    featureRequests: FeatureRequest[];
}> = ({ featureRequests }) => {
    const history = useHistory();
    const location = useLocation();
    const { sortedData, orderBy, orderDirection, handleSort } = useSorting(
        featureRequests,
        "updatedAt"
    );

    return (
        <>
            <Table>
                <thead>
                    <tr>
                        {columns.map((column) => {
                            const isSortable = sortableColumns.includes(
                                column as keyof FeatureRequest
                            );

                            return (
                                <TableHeader
                                    key={column}
                                    scope="col"
                                    sortable={isSortable}
                                    onClick={() =>
                                        isSortable && handleSort(column as keyof FeatureRequest)
                                    }
                                >
                                    {column}
                                    {orderBy === column && (
                                        <SortIndicator direction={orderDirection} />
                                    )}
                                </TableHeader>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {sortedData.map((item) => (
                        <TableRow
                            key={item.id}
                            onClick={() =>
                                history.push(`${location.pathname}?featureRequestId=${item.id}`)
                            }
                        >
                            {columns.map((column) => (
                                <TableCell key={column}>
                                    <FeatureRequestTableRow
                                        valueType={column as keyof FeatureRequest}
                                        item={item}
                                    />
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </tbody>
            </Table>
        </>
    );
};

export default FeatureRequestsTable;
